import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldQuestion, Loader2, AlertTriangle, Phone, MapPin, ArrowRight, RotateCcw, ShieldAlert, ClipboardCheck, CheckCircle2 } from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Button, Alert } from '../../components/portal/ui'
import {
  getRiskFactors,
  assessRisk,
  type RiskFactor,
  type RiskAssessment,
  type RiskFactorLevel,
} from '../../services/riskService'

// Per-band visual treatment. The band is an advisory cue, NEVER a diagnosis or a
// risk measurement — the card copy and permanent disclaimer make that explicit.
const BAND_STYLES: Record<RiskFactorLevel, { ring: string; bar: string; icon: typeof ShieldAlert }> = {
  NONE: { ring: 'ring-emerald-200', bar: 'bg-emerald-500', icon: CheckCircle2 },
  LOW: { ring: 'ring-amber-200', bar: 'bg-amber-500', icon: ClipboardCheck },
  MODERATE: { ring: 'ring-orange-300', bar: 'bg-orange-500', icon: AlertTriangle },
  HIGH: { ring: 'ring-red-300', bar: 'bg-red-500', icon: ShieldAlert },
}

export default function RiskCheckerPage() {
  const { t, i18n } = useTranslation()
  const [factors, setFactors] = useState<RiskFactor[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loadingCatalog, setLoadingCatalog] = useState(true)
  const [assessing, setAssessing] = useState(false)
  const [result, setResult] = useState<RiskAssessment | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [catError, setCatError] = useState<string | null>(null)

  // Load the localised factor catalog once; reload when the UI language changes.
  useEffect(() => {
    let cancelled = false
    setLoadingCatalog(true); setCatError(null)
    getRiskFactors()
      .then((data) => { if (!cancelled) setFactors(data) })
      .catch(() => { if (!cancelled) setCatError(t('pages.riskChecker.catalogError')) })
      .finally(() => { if (!cancelled) setLoadingCatalog(false) })
    return () => { cancelled = true }
  }, [i18n.language, t])

  // Clear a stale (language-specific) result when the language changes.
  useEffect(() => { setResult(null) }, [i18n.language])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    if (result) setResult(null)
  }

  const check = async () => {
    setAssessing(true); setError(null)
    try {
      const data = await assessRisk(Array.from(selected))
      setResult(data)
      requestAnimationFrame(() => document.getElementById('risk-result')?.focus())
    } catch (e: any) {
      setError(e?.response?.data?.message || t('pages.riskChecker.assessError'))
    } finally { setAssessing(false) }
  }

  const reset = () => {
    setSelected(new Set()); setResult(null); setError(null)
    document.getElementById('risk-check')?.focus()
  }

  const band = result ? BAND_STYLES[result.riskLevel] : null

  return (
    <PortalLayout>
      <Seo
        title={t('pages.riskChecker.seo.title')}
        description={t('pages.riskChecker.seo.description')}
        canonicalPath="/am-i-at-risk"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.riskChecker', 'Am I at Risk?'), path: '/am-i-at-risk' }]}
      />

      {/* Hero */}
      <Section className="pb-8">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.riskChecker', 'Am I at Risk?') }]} />
          <SectionHeading as="h1" eyebrow={t('pages.riskChecker.hero.eyebrow')} title={t('pages.riskChecker.hero.title')} description={t('pages.riskChecker.hero.description')} />
          <div className="mt-4 max-w-2xl">
            <Alert tone="warning" title={t('pages.riskChecker.notDiagnosis')}>
              {t('pages.riskChecker.notDiagnosisDesc')}
            </Alert>
          </div>
        </Container>
      </Section>

      {/* Factor checklist */}
      <Section className="bg-white py-8">
        <Container className="max-w-3xl">
          <h2 id="risk-check" tabIndex={-1} className="text-xl font-semibold text-slate-800 mb-1">
            {t('pages.riskChecker.questionTitle')}
          </h2>
          <p className="text-slate-600 mb-6 text-sm">{t('pages.riskChecker.questionHelp')}</p>

          {catError && <Alert tone="warning" title={catError} className="mb-4" />}

          {loadingCatalog ? (
            <div className="flex items-center gap-2 text-slate-500 py-8" role="status">
              <Loader2 className="w-5 h-5 animate-spin" /> {t('pages.riskChecker.loading')}
            </div>
          ) : (
            <ul className="space-y-3" aria-describedby="risk-check">
              {factors.map((f) => {
                const checked = selected.has(f.id)
                return (
                  <li key={f.id}>
                    <label
                      className={`flex gap-3 items-start p-4 rounded-xl border cursor-pointer transition min-h-[44px] bg-white hover:bg-slate-50 ${checked ? 'border-[rgb(15_76_129)] bg-[rgb(15_76_129)]/5 ring-1 ring-[rgb(15_76_129)]/30' : 'border-slate-200'}`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-5 w-5 rounded border-slate-300 text-[rgb(15_76_129)] focus:ring-[rgb(15_76_129)] shrink-0"
                        checked={checked}
                        onChange={() => toggle(f.id)}
                        aria-describedby={f.description ? `rdesc-${f.id}` : undefined}
                      />
                      <span className="flex-1">
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-800">{f.label}</span>
                          {f.highImpact && (
                            <span className="inline-flex items-center rounded-full bg-[rgb(254_226_226)] text-[rgb(127_29_29)] px-2.5 py-0.5 text-xs font-semibold">{t('pages.riskChecker.highImpact')}</span>
                          )}
                        </span>
                        {f.description && (
                          <span id={`rdesc-${f.id}`} className="block text-sm text-slate-500 mt-0.5">{f.description}</span>
                        )}
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button id="risk-check-btn" onClick={check} variant="primary" size="lg" disabled={assessing || loadingCatalog} icon={assessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldQuestion className="w-5 h-5" />} className="min-h-[44px]">
              {assessing ? t('pages.riskChecker.checking') : t('pages.riskChecker.checkBtn')}
            </Button>
            {(selected.size > 0 || result) && (
              <Button onClick={reset} variant="ghost" size="md" icon={<RotateCcw className="w-4 h-4" />}>{t('pages.riskChecker.reset')}</Button>
            )}
            <span className="text-sm text-slate-500" aria-live="polite">
              {t('pages.riskChecker.selectedCount', { count: selected.size })}
            </span>
          </div>
          {error && <Alert tone="warning" title={error} className="mt-4" />}
        </Container>
      </Section>

      {/* Result */}
      {result && band && (
        <Section className="bg-slate-50 py-8">
          <Container className="max-w-3xl">
            <div
              id="risk-result"
              tabIndex={-1}
              className={`card ring-2 ${band.ring} bg-white`}
              aria-labelledby="risk-result-heading"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-white ${band.bar}`} aria-hidden="true">
                  <band.icon className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{t('pages.riskChecker.resultLabel')}</p>
                  <h2 id="risk-result-heading" className="text-lg font-semibold text-slate-800">{result.riskLabel}</h2>
                </div>
              </div>

              <p className="text-slate-700">{result.summary}</p>
              <p className="text-slate-700 mt-3"><span className="font-semibold">{t('pages.riskChecker.recommendationLabel')} </span>{result.recommendation}</p>

              {result.selectedFactors.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">{t('pages.riskChecker.youSelected')}</p>
                  <div className="flex flex-wrap gap-2">
                    {result.selectedFactors.map((label, i) => (
                      <span key={i} className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">{label}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action buttons from the backend */}
              <div className="mt-6 flex flex-wrap gap-3">
                {result.actions.map((a, i) =>
                  a.type === 'CALL' ? (
                    <Button key={i} href={a.href || undefined} variant="accent" size="md" icon={<Phone className="w-4 h-4" />} className="min-h-[44px]">{a.label}</Button>
                  ) : (
                    <Button key={i} to={a.path || undefined} variant="primary" size="md" icon={a.path === '/find-tb-centre' ? <MapPin className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />} className="min-h-[44px]">{a.label}</Button>
                  )
                )}
              </div>
            </div>

            {/* Permanent disclaimer — rendered verbatim from the backend */}
            <Alert tone="warning" title={t('pages.riskChecker.important')} className="mt-4">
              {result.disclaimer}
            </Alert>
          </Container>
        </Section>
      )}
    </PortalLayout>
  )
}